import React, { useState } from "react";
import {
  Workflow,
  Cloud,
  BrainCircuit,
  Activity,
  Layers3,
  ShieldCheck,
  Briefcase,
  LineChart,
  UserCog,
  Rocket,
  Zap,
} from "lucide-react";

const SectionHeader = ({ title }) => (
  <h2 className="text-3xl sm:text-4xl font-extrabold text-center bg-gradient-to-r from-blue-400 via-purple-500 to-indigo-500 bg-clip-text text-transparent uppercase tracking-wide mb-8">
    {title}
  </h2>
);

const FeatureBox = ({ Icon, title, description }) => {
  const [coords, setCoords] = useState({ x: 0, y: 0 });

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    setCoords({ x, y });
  };

  return (
    <div
      className="relative group p-6 rounded-2xl border border-gray-700 bg-zinc-900 overflow-hidden transition-all duration-300 shadow-md"
      onMouseMove={handleMouseMove}
    >
      {/* Moving gradient */}
      <div
        className="absolute w-60 h-60 rounded-full bg-gradient-to-br from-blue-500 via-purple-600 to-indigo-500 opacity-0 group-hover:opacity-30 blur-3xl pointer-events-none transition-opacity duration-100 ease-out"
        style={{
          top: coords.y - 120,
          left: coords.x - 120,
          transition: "top 0.1s ease-out, left 0.1s ease-out",
        }}
      />
      {/* Border glow */}
      <div className="absolute inset-0 rounded-2xl border-2 border-transparent group-hover:border-indigo-500 group-hover:shadow-[0_0_20px_2px_rgba(99,102,241,0.6)] transition-all duration-300 pointer-events-none z-0" />

      {/* Content */}
      <div className="relative z-10">
        <Icon className="w-8 h-8 text-indigo-400 mb-4" />
        <h4 className="text-xl font-semibold mb-2 text-white">{title}</h4>
        <p className="text-gray-300">{description}</p>
      </div>
    </div>
  );
};

const Features = () => {
  return (
    <section className="bg-gradient-to-br from-[#0f0f1a] to-[#1a1a2e] py-20 px-6 sm:px-12">
      <div className="max-w-6xl mx-auto">
        {/* What is Zyntegrate */}
        <SectionHeader title="What is Zyntegrate?" />
        <div className="text-gray-300 text-lg text-center max-w-3xl mx-auto mb-16">
          Zyntegrate is designed for modern enterprises seeking to automate complex workflows and orchestrate data across fragmented systems. Whether you're bridging legacy databases, integrating with cloud apps, or connecting third-party APIs—Zyntegrate makes it simple.
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
          <FeatureBox Icon={Cloud} title="Connect Everything" description="Integrate legacy systems, SaaS, cloud, and APIs in one place." />
          <FeatureBox Icon={Workflow} title="Automate at Scale" description="Build and run intelligent workflows without extensive code." />
          <FeatureBox Icon={ShieldCheck} title="Secure & Compliant" description="Enterprise-grade security and role-based access." />
          <FeatureBox Icon={Activity} title="Real-Time Insights" description="Stream data into analytics or BI tools as it happens." />
        </div>

        {/* Key Features */}
        <SectionHeader title="Key Features" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-20">
          <FeatureBox Icon={Zap} title="Low-Code Designer" description="Drag-and-drop interface for rapid integration development." />
          <FeatureBox Icon={Layers3} title="Prebuilt Connectors" description="Support for Salesforce, SAP, AWS, Azure, SQL, REST & more." />
          <FeatureBox Icon={BrainCircuit} title="AI Mapping & Transformation" description="Normalize and transform data with AI-assisted tools." />
          <FeatureBox Icon={Activity} title="Monitoring & Alerts" description="Live dashboards, logs, and customizable alerts." />
          <FeatureBox Icon={Workflow} title="Versioning & Reusability" description="Manage changes, reuse templates, and roll back easily." />
        </div>

        {/* Built For */}
        <SectionHeader title="Built For" />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-20">
          <FeatureBox Icon={UserCog} title="IT Teams" description="Simplify maintenance and integration with centralized control." />
          <FeatureBox Icon={Briefcase} title="Business Leaders" description="Unlock siloed data for faster, real-time decisions." />
          <FeatureBox Icon={LineChart} title="Data Analysts" description="Accelerate pipelines with unified, accurate data." />
        </div>

        {/* Why Choose Zyntegrate */}
        <SectionHeader title="Why Choose Zyntegrate?" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-20">
          <FeatureBox Icon={Rocket} title="Scalable Architecture" description="Grows with your business." />
          <FeatureBox Icon={Zap} title="Faster Time to Value" description="Launch integrations in days, not months." />
          <FeatureBox Icon={Cloud} title="Vendor-Agnostic" description="Works across any platform or ecosystem." />
          <FeatureBox Icon={Briefcase} title="Cost-Efficient" description="Reduces development and maintenance overhead." />
        </div>
      </div>
    </section>
  );
};

export default Features;